import React from "react"
import { PageDrawerAction } from "src/components/ui/PageDrawerAction"
import { Icons } from "src/components/ui/Icons"
import { useActions, useAppState, useReaction } from "src/state"
import {
    Guideline_Role_Enum,
    Guideline_Status_Enum,
    GuidelineFragment,
    UpdateGuidelineMutationVariables
} from "src/state/gql/_types"
import { FormDrawer } from "src/components/forms/FormDrawer"
import { Loader } from "@mantine/core"

import { extractCurrentItemData, FormSchema } from "src/utils/forms"
import { GuidelinesList } from "src/components/ui/GuidelinesList"
import { GuidelineMetaData } from "src/state/_types"
import { TalentAdminBookingGuidelineInfoFragment, TalentAdminBookingGuidelineTaskFragment, TalentAdminBookingGuidelineQuestionFragment, TalentAdminBookingGuidelineInfoSchema, TalentAdminBookingGuidelineTaskSchema, TalentAdminBookingGuidelineQuestionSchema, TalentAdminBookingGuidelineUpdateFragment } from "./TalentAdminBookingGuidelineSchema"

type BookingGuidelineFragment = TalentAdminBookingGuidelineInfoFragment | TalentAdminBookingGuidelineTaskFragment | TalentAdminBookingGuidelineQuestionFragment;

let initialStateUpdate: TalentAdminBookingGuidelineUpdateFragment = {
    name: "",
    description: "",
    document_id: null,
    talent_required: false,
    talent_confirmed: false,
    talent_completed: false,
    metadata: null,
};

const BookingGuidelineSchemaMap = {
    [Guideline_Role_Enum.Info]: TalentAdminBookingGuidelineInfoSchema,
    [Guideline_Role_Enum.AgencyTask]: TalentAdminBookingGuidelineTaskSchema,
    [Guideline_Role_Enum.AreaTask]: TalentAdminBookingGuidelineTaskSchema,
    [Guideline_Role_Enum.VenueTask]: TalentAdminBookingGuidelineTaskSchema,
    [Guideline_Role_Enum.TalentTask]: TalentAdminBookingGuidelineTaskSchema,
    [Guideline_Role_Enum.TalentQuestion]: TalentAdminBookingGuidelineQuestionSchema,
    [Guideline_Role_Enum.AreaQuestion]: TalentAdminBookingGuidelineQuestionSchema,
    [Guideline_Role_Enum.VenueQuestion]: TalentAdminBookingGuidelineQuestionSchema,
}

export const ProcessTalentAdminBookingGuidelineDrawer: React.FC<{ onClose(): void, deselect: boolean, schema: FormSchema<BookingGuidelineFragment> }> = ({ onClose, deselect = true }) => {


    const { updateItem, deleteItem, deselectItem } = useActions().guidelines;
    const { currentItem, loading, errors } = useAppState().guidelines;

    const currentSchema = React.useMemo(() => {
        return BookingGuidelineSchemaMap[currentItem?.role as Guideline_Role_Enum] || TalentAdminBookingGuidelineInfoSchema;
    }, [currentItem])

    if (loading || !currentItem) return <Loader />

    let currentItemId = null;
    let currentItemData: BookingGuidelineFragment | null = null;
    if (currentItem) {
        currentItemId = currentItem.id;
        currentItemData = extractCurrentItemData<BookingGuidelineFragment>(initialStateUpdate, currentItem);
    }

    const handleClose = () => {
        if (deselect) {
            deselectItem()
        }
        onClose()
    }

    const handleUpdate = (payload: UpdateGuidelineMutationVariables) => {
        return updateItem({
            id: payload.id,
            _set: {
                ...payload._set
            }
        })
    }

    return (
        <>
            <FormDrawer<BookingGuidelineFragment, GuidelineFragment, string>
                autoOpen={false}
                formSchema={currentSchema}
                validators={{
                    compare_fields: async (value: string, context: any) => {
                        let booleanValue = value === 'true';
                        return context.parent.talent_required ? booleanValue : true;
                    },
                    validate_metadata: async (value: string) => {
                        try {
                            let guidelineMetaData = JSON.parse(value) as GuidelineMetaData;
                            let unansweredQuestions = guidelineMetaData.questions.filter(q => q.required && !q.answer);
                            if (unansweredQuestions.length > 0) {
                                return false;
                            }
                            return true;
                        } catch (error) {
                            return true;
                        }
                    }
                }}
                updateAction={handleUpdate}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialStateUpdate}
                currentItemData={currentItemData}
                onClose={handleClose}
                deleteRedirect={true}
                validateOnMount={true}
            />

        </>
    )

}

export const ProcessTalentAdminBookingGuidelineAction = () => {

    const reaction = useReaction()
    const { deselectItem } = useActions().guidelines;
    const [opened, setOpened] = React.useState(false);

    React.useEffect(() => reaction(
        (state) => state.guidelines.currentItemId,
        (selectedID) => {
            setOpened(!!selectedID)
        }, { immediate: false }
    ))

    const handleClose = () => {
        deselectItem()
    }

    return (
        <PageDrawerAction<GuidelineFragment>
            title='Review Guideline'
            action={ProcessTalentAdminBookingGuidelineDrawer}
            icon={Icons.edit}
            startOpened={opened}
            onClose={handleClose}
            hidden
        />
    )
}

export function TalentAdminBookingGuidelineActions() {

    const { currentItem: currentBooking } = useAppState(state => state.bookings)
    const { itemsList: guidelines } = useAppState(state => state.guidelines)
    const { subscribeToItems, unsubscribeFromItems } = useActions().guidelines;

    React.useEffect(() => {
        if (currentBooking) {
            subscribeToItems({
                where: {
                    booking_id: { _eq: currentBooking.id },
                    status: { _eq: Guideline_Status_Enum.Active },
                    talent_required: { _eq: true },
                    _or: [
                        { target_booking_status: { _is_null: true } },
                        { target_booking_status: { _in: [currentBooking.status] } }
                    ]
                },
                limit: 1000
            })
            return () => {
                unsubscribeFromItems()
            }
        }
    }, [currentBooking, subscribeToItems, unsubscribeFromItems])

    const tasks = React.useMemo(() => {
        return guidelines.filter(g => g.role === Guideline_Role_Enum.AgencyTask || g.role === Guideline_Role_Enum.AreaTask || g.role === Guideline_Role_Enum.VenueTask || g.role === Guideline_Role_Enum.TalentTask);
    }, [guidelines])

    const questions = React.useMemo(() => {
        return guidelines.filter(g => g.role === Guideline_Role_Enum.AreaQuestion || g.role === Guideline_Role_Enum.VenueQuestion || g.role === Guideline_Role_Enum.TalentQuestion);
    }, [guidelines])

    const info = React.useMemo(() => {
        return guidelines.filter(g => g.role === Guideline_Role_Enum.Info);
    }, [guidelines])

    return (
        <>
            <ProcessTalentAdminBookingGuidelineAction />
            <GuidelinesList
                title="Tasks"
                icon="ph:list-checks-duotone"
                guidelines={tasks}
                track_talent={true}
            />
            <GuidelinesList
                title="Questions"
                icon="ph:question-duotone"
                guidelines={questions}
                track_talent={true}
            />
            <GuidelinesList
                title="Important Information"
                icon="ph:clipboard-text-duotone"
                guidelines={info}
                track_talent={true}
            />
        </>
    )

}